import type { FieldConflict } from '../../../types';

// Human-readable labels for task/column/project fields
const FIELD_LABELS: Record<string, string> = {
  title: 'Title',
  notes: 'Notes',
  column_id: 'Column',
  due_date: 'Due date',
  priority: 'Priority',
  tags: 'Tags',
  assignee: 'Assignee',
  subtasks: 'Subtasks',
  cost: 'Cost',
  time: 'Time',
  comments: 'Comments',
  attachments: 'Attachments',
  color: 'Colour',
  wip_limit: 'WIP limit',
  name: 'Name',
  category: 'Category',
  archived: 'Archived',
};

function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a === null || b === null) return a === b;
  if (typeof a !== typeof b) return false;
  if (typeof a !== 'object') return false;
  return JSON.stringify(a) === JSON.stringify(b);
}

export interface MergeResult {
  autoResolved: Record<string, unknown>;
  conflicts: FieldConflict[];
}

/**
 * Three-way merge of a single entity.
 *
 * @param base   Field values at the time the offline snapshot was taken
 * @param local  Field values as the user changed them offline (from syncQueue data)
 * @param server Current field values on the server
 */
export function threeWayMerge(
  base: Record<string, unknown>,
  local: Record<string, unknown>,
  server: Record<string, unknown>,
): MergeResult {
  const autoResolved: Record<string, unknown> = {};
  const conflicts: FieldConflict[] = [];

  // Collect all keys across all three versions
  const allKeys = new Set([...Object.keys(base), ...Object.keys(local), ...Object.keys(server)]);

  for (const field of allKeys) {
    const baseVal = base[field];
    const localVal = local[field];
    const serverVal = server[field];

    const localChanged = !deepEqual(baseVal, localVal);
    const serverChanged = !deepEqual(baseVal, serverVal);

    if (!localChanged && !serverChanged) {
      // Neither side changed — keep base/server (they're the same)
      continue;
    }

    if (localChanged && !serverChanged) {
      // Only local changed — use local value
      autoResolved[field] = localVal;
      continue;
    }

    if (!localChanged && serverChanged) {
      // Only server changed — use server value (already on server, nothing to push)
      continue;
    }

    // Both changed — true conflict
    if (deepEqual(localVal, serverVal)) {
      // Both changed to the same value — no conflict
      continue;
    }

    conflicts.push({
      field,
      label: FIELD_LABELS[field] ?? field,
      baseValue: baseVal,
      localValue: localVal,
      serverValue: serverVal,
      resolution: null,
    });
  }

  return { autoResolved, conflicts };
}

export function formatFieldValue(value: unknown): string {
  if (value === null || value === undefined) return '(empty)';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'string') return value || '(empty)';
  if (typeof value === 'number') return String(value);
  if (Array.isArray(value)) {
    if (value.length === 0) return '(none)';
    // Tags
    if (value[0] && typeof value[0] === 'object' && 'label' in (value[0] as object)) {
      return (value as Array<{ label: string }>).map((t) => t.label).join(', ');
    }
    return `${value.length} item${value.length !== 1 ? 's' : ''}`;
  }
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }
  return String(value);
}
